/**
 * Scroll reveal for ASLF Canine sections.
 *
 * Motion matches the ASLF Formulas storefront (aslf-home.js / .aslf-rv):
 * fade plus a 28px rise, 0.7s ease, IntersectionObserver threshold 0.12,
 * root margin 0px 0px -6% 0px, stagger delays 0.08s / 0.16s / 0.24s.
 *
 * Content stays visible until this script adds `canine-reveal` on <html>.
 * The hero is excluded. Elements already in view are marked before that
 * class is applied so the first screen does not flash.
 */
(function () {
  const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';
  const DESKTOP_QUERY = '(min-width: 990px)';
  const THRESHOLD = 0.12;
  const ROOT_MARGIN = '0px 0px -6% 0px';
  const STAGGER = ['', 'canine-rv-d1', 'canine-rv-d2', 'canine-rv-d3'];
  const HERO_SELECTOR = '.canine-hero, .canine-hero-section';

  if (window.matchMedia(REDUCE_QUERY).matches || !('IntersectionObserver' in window)) return;

  /** @type {IntersectionObserver | null} */
  let observer = null;

  const revealRoot = () => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) return null;
    return document.querySelector('.page-wrapper');
  };

  /**
   * @param {HTMLElement} el
   * @param {Element | null} root
   */
  const isInView = (el, root) => {
    const bounds = el.getBoundingClientRect();
    if (bounds.height === 0) return false;

    const rootBounds = root
      ? root.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };

    const inset = (rootBounds.bottom - rootBounds.top) * 0.06;
    const viewTop = rootBounds.top;
    const viewBottom = rootBounds.bottom - inset;
    const visibleTop = Math.max(bounds.top, viewTop);
    const visibleBottom = Math.min(bounds.bottom, viewBottom);
    const visible = Math.max(0, visibleBottom - visibleTop);

    return visible / bounds.height >= THRESHOLD;
  };

  const ensureObserver = () => {
    if (observer) observer.disconnect();

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('in');
          observer?.unobserve(entry.target);
        }
      },
      {
        threshold: THRESHOLD,
        root: revealRoot(),
        rootMargin: ROOT_MARGIN,
      }
    );

    return observer;
  };

  /**
   * @param {Iterable<Element>} elements
   */
  const arm = (elements) => {
    const root = revealRoot();
    /** @type {HTMLElement[]} */
    const pending = [];

    for (const el of elements) {
      if (!(el instanceof HTMLElement)) continue;
      if (el.closest(HERO_SELECTOR)) continue;
      if (el.hasAttribute('data-canine-rv')) continue;

      el.setAttribute('data-canine-rv', '1');
      pending.push(el);
    }

    if (!pending.length) return;

    for (const el of pending) {
      if (isInView(el, root)) el.classList.add('in');
    }

    document.documentElement.classList.add('canine-reveal');

    const io = ensureObserver();

    for (const el of document.querySelectorAll('.canine-rv[data-canine-rv]:not(.in)')) {
      io.observe(el);
    }
  };

  /**
   * @param {ParentNode} scope
   */
  const tagRecommendations = (scope) => {
    const sections =
      scope instanceof Element && scope.matches('.product-recommendations')
        ? [scope]
        : scope.querySelectorAll('.product-recommendations');

    for (const section of sections) {
      const nodes = section.querySelectorAll('h1, h2, h3, .product-card');
      let index = 0;

      for (const node of nodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.closest(HERO_SELECTOR)) continue;
        if (node.closest('.product-card') && !node.classList.contains('product-card')) continue;
        if (node.classList.contains('canine-rv')) continue;

        node.classList.add('canine-rv');
        const delay = STAGGER[index % STAGGER.length];
        if (delay) node.classList.add(delay);
        index += 1;
      }
    }
  };

  /**
   * @param {ParentNode} [root]
   */
  const init = (root) => {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    tagRecommendations(scope);
    arm(scope.querySelectorAll('.canine-rv'));
  };

  const watchRecommendations = () => {
    for (const section of document.querySelectorAll('.product-recommendations')) {
      if (!(section instanceof HTMLElement)) continue;
      if (section.hasAttribute('data-canine-reveal-watch')) continue;

      section.setAttribute('data-canine-reveal-watch', '1');
      const mo = new MutationObserver(() => {
        init(section);
      });
      mo.observe(section, { childList: true, subtree: true });
    }
  };

  const start = () => {
    init(document);
    watchRecommendations();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    init(target);
    watchRecommendations();
  });

  window.matchMedia(DESKTOP_QUERY).addEventListener('change', () => {
    const root = revealRoot();

    for (const el of document.querySelectorAll('.canine-rv[data-canine-rv]:not(.in)')) {
      if (el instanceof HTMLElement && isInView(el, root)) el.classList.add('in');
    }

    const io = ensureObserver();

    for (const el of document.querySelectorAll('.canine-rv[data-canine-rv]:not(.in)')) {
      io.observe(el);
    }
  });
})();
